"use client"

import { useState, useRef, useEffect, useCallback } from "react"
import { Modal, ModalHeader, ModalBody, Button, Alert, Spinner, Row, Col, Card, CardBody } from "reactstrap"
import {
  FaPrint,
  FaQrcode,
  FaIdCard,
  FaCheck,
  FaTimes,
  FaExclamationTriangle,
} from "react-icons/fa"
import { toast } from "react-toastify"
import QRCode from "qrcode"
import { useSecurePrint } from "../../../hooks/useSecurePrint"
import { renderBadge, prepareBadgeCanvas, BADGE_WIDTH as SHARED_BADGE_WIDTH, BADGE_HEIGHT as SHARED_BADGE_HEIGHT } from "./badgeRenderer"

// Create a helper function for dynamic images (replace with your actual implementation)
const dynamicImage = (imagePath: string): string => {
  return `/assets/images/${imagePath}`
}

interface BadgeInfo {
  person_full_name: string
  role: string
  exhibitor_name: string
  exhibition_title: string
  exhibition_description: string
  exhibition_venue?: string
  start_date: string
  end_date: string
  exhibition_phone?: string
  exhibition_email?: string
  stand_category: string
  print_type: string
  sequence_number: number
  verification_code: string
  generated_at: string
  id_doc_number?: string
  email?: string
  phone?: string
  role_color?: string
}

interface VerificationPayload {
  verification_code: string
}

interface BadgeData {
  badge_info: BadgeInfo
  verification_payload: VerificationPayload
  photo_url: string | null
  has_photo: boolean
}

interface TemplateConfig {
  width: number
  height: number
  colors: {
    primary: string
    secondary: string
    background: string
    text: string
  }
  fonts: {
    title: { size: number; weight: string }
    name: { size: number; weight: string }
    role: { size: number; weight: string }
    info: { size: number; weight: string }
    small: { size: number; weight: string }
  }
  layout: {
    photo_size: number
    photo_position: { x: number; y: number }
    qr_size: number
    show_qr: boolean
    show_logo: boolean
  }
}

interface Props {
  isOpen: boolean
  onToggle: () => void
  badgeData: BadgeData | null
  templateConfig?: TemplateConfig | null
  onComplete: (badgeImage: string) => void
  /** PersonBadgeTracking.id for the badge currently being previewed — required to open a secure print job. */
  badgeTrackingId?: number | string | null
  /** Fired once the secure print job for this badge actually succeeds (not just renders). */
  onPrintSuccess?: () => void
}

const EnhancedPSFBadgeDesigner = ({
  isOpen,
  onToggle,
  badgeData,
  templateConfig,
  onComplete,
  badgeTrackingId,
  onPrintSuccess,
}: Props) => {
  const canvasRef = useRef<HTMLImageElement>(null)
  const hiddenCanvasRef = useRef<HTMLCanvasElement>(null)
  const [badgeImage, setBadgeImage] = useState<string | null>(null)
  const [qrCodeImage, setQrCodeImage] = useState<string | null>(null) // QR code as data URL string
  const [qrImageElement, setQrImageElement] = useState<HTMLImageElement | null>(null) // QR code as Image object
  const [copied, setCopied] = useState(false)
  const [isRendering, setIsRendering] = useState(false)
  const [photoLoaded, setPhotoLoaded] = useState(false)
  const [photoImage, setPhotoImage] = useState<HTMLImageElement | null>(null)
  const [backgroundImage, setBackgroundImage] = useState<HTMLImageElement | null>(null)
  const [isGenerationComplete, setIsGenerationComplete] = useState(false)
  const [shouldClose, setShouldClose] = useState(false)
  const [exhibitionText, setExhibitionText] = useState<string>("")
  const [showLogo, setShowLogo] = useState(false)

  // --- Secure (QZ Tray) printing state ---
  const { state: printState, printBadgeSecurely, discoverPrinters, reset: resetPrintState } = useSecurePrint()
  const [availablePrinters, setAvailablePrinters] = useState<string[]>([])
  const [selectedPrinter, setSelectedPrinter] = useState<string>("")
  const [printersError, setPrintersError] = useState<string>("")

  // Badge dimensions (portrait with increased height) — sourced from the
  // shared renderer so single print can never drift from bulk print again.
  const BADGE_WIDTH = SHARED_BADGE_WIDTH
  const BADGE_HEIGHT = SHARED_BADGE_HEIGHT
  const DPI_SCALE = 6

  // Add PSF logo state
  const [psfLogoImage, setPsfLogoImage] = useState<HTMLImageElement | null>(null)

  // ---------- FIX 1: Refs for callback stability ----------
  const onPrintSuccessRef = useRef(onPrintSuccess)
  useEffect(() => {
    onPrintSuccessRef.current = onPrintSuccess
  }, [onPrintSuccess])

  const onToggleRef = useRef(onToggle)
  useEffect(() => {
    onToggleRef.current = onToggle
  }, [onToggle])

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setBadgeImage(null)
      setQrCodeImage(null)
      setQrImageElement(null)
      setCopied(false)
      setPhotoLoaded(false)
      setPhotoImage(null)
      setBackgroundImage(null)
      setPsfLogoImage(null)
      setIsGenerationComplete(false)
      setExhibitionText("") // Reset exhibitionText when modal closes
      resetPrintState()
    }
  }, [isOpen, resetPrintState])

  // Discover printers as soon as the designer opens, so the operator can
  // pick one before clicking "Print Badge". Failure here is non-fatal —
  // it just means QZ Tray isn't reachable yet; the error is surfaced when
  // the operator actually tries to print.
  useEffect(() => {
    if (!isOpen) {
      setAvailablePrinters([])
      setPrintersError("")
      return
    }
    let cancelled = false
    discoverPrinters()
      .then((printers) => {
        if (cancelled) return
        const names = printers.map((p) => p.name)
        setAvailablePrinters(names)
        setPrintersError("")
        setSelectedPrinter((current) => current || names[0] || "")
      })
      .catch((err) => {
        if (cancelled) return
        setPrintersError(err?.message || "QZ Tray is not reachable. Install/start QZ Tray to print.")
      })
    return () => {
      cancelled = true
    }
  }, [isOpen, discoverPrinters])

  // Load background image and PSF logo
  useEffect(() => {
    if (isOpen) {
      const loadImages = async () => {
        // Load PSF Logo
        const logoImg = new window.Image()
        logoImg.crossOrigin = "anonymous"
        const logoPromise = new Promise<HTMLImageElement | null>((resolve) => {
          logoImg.onload = () => resolve(logoImg)
          logoImg.onerror = () => {
            console.warn("PSF logo failed to load")
            resolve(null) // Resolve with null on error
          }
          logoImg.src = dynamicImage("logo.PNG")
        })

        // Load Background Image (convention.png - though not used in the crown design, keep for consistency if it was intended)
        const bgImg = new window.Image()
        bgImg.crossOrigin = "anonymous"
        const bgPromise = new Promise<HTMLImageElement | null>((resolve) => {
          bgImg.onload = () => resolve(bgImg)
          bgImg.onerror = () => {
            console.warn("Background image failed to load")
            resolve(null)
          }
          bgImg.src = "/assets/images/convention.png"
        })

        const [loadedLogo, loadedBg] = await Promise.all([logoPromise, bgPromise])
        setPsfLogoImage(loadedLogo)
        setBackgroundImage(loadedBg)
      }
      loadImages()
    }
  }, [isOpen])

  // Generate high-quality QR code (as data URL string)
  useEffect(() => {
    if (isOpen && badgeData) {
      const qrData = badgeData.badge_info.verification_code
      QRCode.toDataURL(qrData, {
        width: 100 * DPI_SCALE, // Appropriate size for portrait
        margin: 2,
        color: {
          dark: "#000000",
          light: "#ffffff",
        },
        errorCorrectionLevel: "H",
      })
        .then(setQrCodeImage)
        .catch((error) => {
          console.error("QR Code generation failed:", error)
          toast.error("QR Code generation failed")
        })
    }
  }, [badgeData, isOpen])

  // Load QR Code image element from data URL string
  useEffect(() => {
    if (qrCodeImage) {
      const img = new window.Image()
      img.crossOrigin = "anonymous"
      img.onload = () => setQrImageElement(img)
      img.onerror = () => {
        console.warn("QR image element failed to load")
        setQrImageElement(null)
      }
      img.src = qrCodeImage
    } else {
      setQrImageElement(null)
    }
  }, [qrCodeImage])

  useEffect(() => {
    if (shouldClose && isGenerationComplete) {
      onToggle()
      setShouldClose(false)
    }
  }, [shouldClose, isGenerationComplete, onToggle])

  // Load photo with higher quality
  useEffect(() => {
    if (isOpen && badgeData?.has_photo && badgeData.photo_url) {
      const img = new window.Image()
      img.crossOrigin = "anonymous"
      img.onload = () => {
        setPhotoImage(img)
        setPhotoLoaded(true)
      }
      img.onerror = () => {
        setPhotoLoaded(true) // Still set true even on error to proceed
        console.error("Error loading photo")
      }
      img.src = badgeData.photo_url
    } else if (badgeData) {
      setPhotoLoaded(true) // No photo, so it's "loaded"
    }
  }, [badgeData, isOpen])

  // Enhanced badge generation matching crown design exactly
  const generateBadge = useCallback(() => {
    if (!badgeData || !hiddenCanvasRef.current || !qrImageElement) return // Basic checks

    // Conditional checks for images
    if (badgeData.has_photo && !photoImage) return // If photo is expected but not loaded
    if (showLogo && !psfLogoImage) return // If logo is expected but not loaded

    setIsRendering(true)
    const canvas = hiddenCanvasRef.current

    try {
      // Shared setup (dimensions, DPI scale, smoothing) — identical to bulk print.
      const ctx = prepareBadgeCanvas(canvas, DPI_SCALE)

      // Single call into the shared renderer — this is the ONLY badge
      // drawing implementation in the whole app now, so single print and
      // bulk print are guaranteed to produce pixel-identical layouts.
      renderBadge(
        ctx,
        {
          badge_info: {
            person_full_name: badgeData.badge_info.person_full_name,
            role: badgeData.badge_info.role,
            exhibitor_name: badgeData.badge_info.exhibitor_name,
            exhibition_title: badgeData.badge_info.exhibition_title,
            stand_category: badgeData.badge_info.stand_category,
            role_color: badgeData.badge_info.role_color,
          },
          has_photo: badgeData.has_photo,
        },
        {
          photoImage,
          qrImage: qrImageElement,
          logoImage: psfLogoImage,
          showLogo,
        },
      )

      // Generate final badge image
      const dataURL = canvas.toDataURL("image/png", 1.0)
      setBadgeImage(dataURL)
      onComplete(dataURL)
      setIsGenerationComplete(true)
      setIsRendering(false)
    } catch (error) {
      console.error("Badge generation failed:", error)
      toast.error("Failed to generate badge")
      setIsRendering(false)
    }
  }, [badgeData, photoImage, psfLogoImage, qrImageElement, showLogo])

  // Trigger badge generation when all data and images are ready
  useEffect(() => {
    const allImagesLoaded = photoLoaded && (showLogo ? psfLogoImage !== null : true) && qrImageElement !== null
    if (isOpen && badgeData && allImagesLoaded && !isRendering) {
      setExhibitionText(badgeData.badge_info.exhibition_title)
      generateBadge()
    }
  }, [isOpen, badgeData, photoLoaded, psfLogoImage, qrImageElement, showLogo, generateBadge, isRendering])

  const handleSecurePrint = async () => {
    if (!badgeImage || !badgeData) return
    if (!badgeTrackingId) {
      toast.error("Missing badge reference — cannot open a print job for this badge.")
      return
    }
    if (!selectedPrinter) {
      toast.error("Select a printer before printing.")
      return
    }

    await printBadgeSecurely({
      badgeTrackingId,
      badgeImageDataUrl: badgeImage,
      printerName: selectedPrinter,
      personName: badgeData.badge_info.person_full_name,
    })
  }

  // ---------- FIX 2: Stable effect + modal close on success ----------
  useEffect(() => {
    if (printState.phase === "success") {
      toast.success("Badge sent to printer and printed successfully!")
      onPrintSuccessRef.current?.()
      // Close modal automatically, exactly like bulk print
      onToggleRef.current()
    } else if (printState.phase === "error") {
      toast.error(printState.message || "Printing failed.")
    }
    // Depend only on phase; callbacks are read from refs
  }, [printState.phase])

  const viewQRCode = () => {
    if (qrCodeImage && badgeData) {
      const qrWindow = window.open("", "_blank")
      if (qrWindow) {
        qrWindow.document.write(`
          <!DOCTYPE html>
          <html>
            <head>
              <title>PSF QR Verification - ${badgeData.badge_info.person_full_name}</title>
              <style>
                body {
                  display: flex;
                  justify-content: center;
                  align-items: center;
                  min-height: 100vh;
                  background: linear-gradient(135deg, #ff6b35 0%, #22c55e 100%);
                  margin: 0;
                  font-family: Arial, sans-serif;
                  padding: 20px;
                }
                .qr-container {
                  text-align: center;
                  padding: 40px;
                  background: white;
                  border-radius: 20px;
                  box-shadow: 0 20px 40px rgba(0,0,0,0.1);
                  max-width: 500px;
                  width: 100%;
                }
                h1 {
                  color: #ff6b35;
                  margin-bottom: 10px;
                  font-weight: bold;
                  font-size: 28px;
                }
                .subtitle {
                  color: #22c55e;
                  margin-bottom: 30px;
                  font-size: 16px;
                  font-weight: bold;
                }
                .qr-image {
                  border: 3px solid #22c55e;
                  border-radius: 15px;
                  margin: 20px 0;
                  box-shadow: 0 4px 12px rgba(0,0,0,0.1);
                }
                .info-section {
                  background: linear-gradient(135deg, #fff5f0 0%, #f0fdf4 100%);
                  border-radius: 15px;
                  padding: 25px;
                  margin-top: 25px;
                  text-align: left;
                }
                .verification-highlight {
                  background: linear-gradient(135deg, #ff6b35 0%, #22c55e 100%);
                  color: white;
                  padding: 15px;
                  border-radius: 10px;
                  margin-top: 20px;
                  font-weight: bold;
                  text-align: center;
                }
              </style>
            </head>
            <body>
              <div class="qr-container">
                <h1>🔒PSF QR Verification</h1>
                <p class="subtitle">Private Sector Federation - Rwanda</p>
                <img src="${qrCodeImage}" alt="Verification QR Code" width="220" height="220" class="qr-image" />
                <div class="info-section">
                  <h3 style="color: #ff6b35;">Badge Information</h3>
                  <p><strong>Name:</strong> ${badgeData.badge_info.person_full_name}</p>
                  <p><strong>Role:</strong> ${badgeData.badge_info.role}</p>
                  <p><strong>Company:</strong> ${badgeData.badge_info.exhibitor_name}</p>
                  <p><strong>Exhibition:</strong> ${badgeData.badge_info.exhibition_title}</p>
                  <p><strong>Stand Category:</strong> ${badgeData.badge_info.stand_category}</p>
                  ${badgeData.badge_info.exhibition_venue ? `<p><strong>Venue:</strong> ${badgeData.badge_info.exhibition_venue}</p>` : ""}
                  <div class="verification-highlight">
                    🔐 Verification Code: ${badgeData.badge_info.verification_code}
                  </div>
                </div>
              </div>
            </body>
          </html>
        `)
        qrWindow.document.close()
      }
    }
  }

  return (
    <>
      {/* Hidden high-resolution canvas for generation */}
      <canvas ref={hiddenCanvasRef} style={{ display: "none" }} />
      <Modal isOpen={isOpen} toggle={onToggle} size="xl" centered>
        <ModalHeader
          toggle={onToggle}
          className="text-white border-0"
          style={{ background: "linear-gradient(135deg, #ff6b35 0%, #22c55e 100%)" }}
        >
          <div className="d-flex align-items-center">
            <FaIdCard className="me-3" size={24} />
            <div>
              <h4 className="mb-0">✨PSF Rwanda Badge Designer</h4>
              {badgeData && (
                <small className="opacity-75">
                  {badgeData.badge_info.person_full_name} • {badgeData.badge_info.print_type.toUpperCase()}
                </small>
              )}
            </div>
          </div>
        </ModalHeader>
        <ModalBody className="p-0">
          {badgeData ? (
            <div>
              <Row className="g-0">
                <Col lg={7} className="p-4">
                  <div className="position-relative">
                    <div className="text-center">
                      {badgeImage ? (
                        <img
                          ref={canvasRef}
                          src={badgeImage || "/placeholder.svg"}
                          alt="PSF Badge"
                          className="img-fluid shadow-lg"
                          style={{
                            maxWidth: "400px",
                            maxHeight: "600px", // Portrait orientation
                            borderRadius: "12px",
                            border: "3px solid #ffffff",
                            boxShadow: "0 8px 25px rgba(0,0,0,0.15)",
                          }}
                        />
                      ) : (
                        <div
                          className="d-flex flex-column justify-content-center align-items-center bg-white shadow-lg"
                          style={{
                            width: "400px",
                            height: "600px", // Portrait orientation
                            borderRadius: "12px",
                            border: "3px solid #ff6b35",
                            margin: "0 auto",
                          }}
                        >
                          <Spinner style={{ color: "#ff6b35" }} size="lg" />
                          <p className="mt-3 text-muted">Generating PSF badge...</p>
                        </div>
                      )}
                    </div>
                    {isRendering && (
                      <div
                        className="position-absolute top-0 start-0 w-100 h-100 d-flex flex-column justify-content-center align-items-center"
                        style={{
                          background: "rgba(255, 255, 255, 0.95)",
                          borderRadius: "12px",
                          backdropFilter: "blur(5px)",
                        }}
                      >
                        <Spinner style={{ color: "#ff6b35" }} size="lg" />
                        <p className="mt-3 text-dark fw-medium">Rendering PSF badge...</p>
                      </div>
                    )}
                  </div>
                </Col>
                <Col lg={5} className="bg-white border-start">
                  <div className="p-4 h-100">
                    <div className="space-y-4">
                      {/* Logo Control */}
                      <Card className="border-0 shadow-sm" style={{ borderLeft: "4px solid #f59e0b" }}>
                        <CardBody className="p-3">
                          <h6 className="fw-bold mb-3 d-flex align-items-center" style={{ color: "#f59e0b" }}>
                            🎛️ Badge Configuration
                          </h6>
                          <div className="space-y-3">
                            <div className="d-flex align-items-center justify-content-between">
                              <div>
                                <span className="fw-medium">PSF Logo</span>
                                <br />
                                <small className="text-muted">Include PSF logo in green section</small>
                              </div>
                              <div className="form-check form-switch">
                                <input
                                  className="form-check-input"
                                  type="checkbox"
                                  id="logoToggle"
                                  checked={showLogo}
                                  onChange={(e) => setShowLogo(e.target.checked)}
                                />
                                <label className="form-check-label" htmlFor="logoToggle">
                                  {showLogo ? "Enabled" : "Disabled"}
                                </label>
                              </div>
                            </div>
                            <div className="d-flex align-items-center">
                              <span className={`badge ${showLogo ? "bg-success" : "bg-secondary"} me-2`}>
                                {showLogo ? "✓" : "○"}
                              </span>
                              <span className="small">
                                {showLogo ? "Version 2: With PSF logo layout" : "Version 1: Clean layout without logo"}
                              </span>
                            </div>
                          </div>
                        </CardBody>
                      </Card>

                      {/* Printer selection + Print action */}
                      <Card className="border-0 shadow-sm" style={{ borderLeft: "4px solid #ff6b35" }}>
                        <CardBody className="p-3">
                          <label className="form-label small text-muted mb-1">Printer</label>
                          <select
                            className="form-select form-select-sm mb-2"
                            value={selectedPrinter}
                            onChange={(e) => setSelectedPrinter(e.target.value)}
                            disabled={
                              printState.phase !== "idle" &&
                              printState.phase !== "error" &&
                              printState.phase !== "success"
                            }
                          >
                            {availablePrinters.length === 0 && <option value="">No printers found</option>}
                            {availablePrinters.map((name) => (
                              <option key={name} value={name}>
                                {name}
                              </option>
                            ))}
                          </select>
                          {printersError && (
                            <small className="text-danger d-block mb-2">
                              <FaExclamationTriangle className="me-1" />
                              {printersError}
                            </small>
                          )}

                          <Button
                            onClick={handleSecurePrint}
                            className="d-flex align-items-center justify-content-center w-100 px-4 py-2 fw-medium"
                            style={{
                              background: "linear-gradient(135deg, #ff6b35 0%, #e55a2b 100%)",
                              border: "none",
                            }}
                            disabled={
                              !badgeImage ||
                              !selectedPrinter ||
                              ["checking-printer", "uploading", "retrieving", "printing"].includes(printState.phase)
                            }
                          >
                            {["checking-printer", "uploading", "retrieving", "printing"].includes(
                              printState.phase,
                            ) ? (
                              <Spinner size="sm" className="me-2" />
                            ) : (
                              <FaPrint className="me-2" />
                            )}
                            Print Badge
                          </Button>

                          {printState.phase !== "idle" && (
                            <div
                              className={`small fw-medium mt-2 ${
                                printState.phase === "success"
                                  ? "text-success"
                                  : printState.phase === "error"
                                  ? "text-danger"
                                  : "text-primary"
                              }`}
                            >
                              {["checking-printer", "uploading", "retrieving", "printing"].includes(
                                printState.phase,
                              ) && <Spinner size="sm" className="me-2" />}
                              {printState.phase === "success" && <FaCheck className="me-2" />}
                              {printState.phase === "error" && <FaExclamationTriangle className="me-2" />}
                              {printState.message}
                            </div>
                          )}
                        </CardBody>
                      </Card>
                    </div>
                  </div>
                </Col>
              </Row>
              {!badgeImage && !isRendering && (
                <Alert color="warning" className="m-4 text-center">
                  <FaExclamationTriangle className="me-2" />
                  <strong>Preparing crown-style PSF badge generation...</strong>
                  <br />
                  <small>Loading resources and preparing crown design with connected sections</small>
                </Alert>
              )}
            </div>
          ) : (
            <div className="p-5 text-center">
              <Alert color="danger" className="border-0 shadow-sm">
                <div className="d-flex flex-column align-items-center">
                  <FaTimes size={48} className="text-danger mb-3" />
                  <h4 className="text-danger fw-bold">No PSF Badge Data Available</h4>
                  <p className="mb-0 text-muted">
                    Please provide valid badge information to generate a crown-style PSF Rwanda badge.
                  </p>
                </div>
              </Alert>
            </div>
          )}
        </ModalBody>
      </Modal>
      <style>{`
        .space-y-4 > * + * {
          margin-top: 1rem;
        }
        .space-y-2 > * + * {
          margin-top: 0.5rem;
        }
      `}</style>
    </>
  )
}

export default EnhancedPSFBadgeDesigner